import './lineReveal.scss';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const HOVER_SCALE_IN = .24;
const HOVER_SCALE_OUT = .2;
// Large enough to read as a genuine slide-in (per docs/gsap-animation-spec.md
// §3 — the reference video moves the work-listing view-more link/rule several
// hundred px, not a small nudge).
const CONTENT_OFFSET = 240;

// Shared "link/heading + line" system used by every `*__header`, `*__footer`,
// and `*__related-*` rule-and-link pairing across the theme (work listing,
// news listing, single-post related block, footer nav). Driven entirely by
// data attributes so new instances don't need new JS:
//
//   [data-line-group][data-line-side="left|right"]   wraps one pairing
//     [data-line-rule]      the rule/bar that grows in
//     [data-line-content]   the link or heading that slides in alongside it
//
//   [data-slide-in="left|right"]   a solo text/heading entrance, no rule
//
//   [data-line-hover] retains a subtle text scale, without a circle.
function attachHoverLink(link) {
  const mm = gsap.matchMedia();
  mm.add(HOVER_QUERY, () => {
    const hoverIn = () => gsap.to(link, {scale: 1.04, duration: HOVER_SCALE_IN, ease: 'power2.out'});
    const hoverOut = () => gsap.to(link, {scale: 1, duration: HOVER_SCALE_OUT, ease: 'power2.out'});
    link.addEventListener('mouseenter', hoverIn);
    link.addEventListener('mouseleave', hoverOut);
    link.addEventListener('focus', hoverIn);
    link.addEventListener('blur', hoverOut);
    return () => {
      link.removeEventListener('mouseenter', hoverIn);
      link.removeEventListener('mouseleave', hoverOut);
      link.removeEventListener('focus', hoverIn);
      link.removeEventListener('blur', hoverOut);
    };
  });
}
function initHoverLinks(reducedMotion) {
  if (!reducedMotion) gsap.utils.toArray('[data-line-hover]').forEach(attachHoverLink);
}

function initLineGroups(reducedMotion) {
  gsap.utils.toArray('[data-line-group]').forEach((group) => {
    const rule = group.querySelector('[data-line-rule]');
    const content = group.querySelector('[data-line-content]');
    const side = group.dataset.lineSide === 'right' ? 'right' : 'left';
    const isLink = content && content.tagName === 'A';

    if (!rule && !content) return;

    if (reducedMotion) {
      return;
    }

    // One rule, one mechanism, direction is the only variable: `side` names
    // the physical edge the rule sits on/grows from. A link is "pushed" by
    // its rule — both anchored on `side`, the rule's growing tip carrying
    // the link along with it — regardless of which one happens to render
    // first in the DOM (work-listing__footer: rule then link;
    // news-listing__footer/bridge-contact-action: link then rule — same
    // animation, `side` is what differs, not the mechanism). A headline-only
    // pair instead has the rule anchor the OPPOSITE edge, so its tip grows
    // TOWARD the text and the two converge/meet instead of traveling
    // together.
    const ruleOrigin = isLink ? side : (side === 'left' ? 'right' : 'left');
    let offset = side === 'right' ? CONTENT_OFFSET : -CONTENT_OFFSET;

    if (isLink && rule && content) {
      // Measured BEFORE any transform is applied, while both still sit in
      // their natural resting layout: the link starts exactly at the rule's
      // own anchor edge (`side`) — where the line's growth begins — and
      // travels the real distance to its resting spot, not a guessed fixed
      // number, so it reads as the line's tip physically carrying it in.
      const ruleRect = rule.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      offset = side === 'right'
        ? ruleRect.right - contentRect.right
        : ruleRect.left - contentRect.left;
    }

    if (rule) gsap.set(rule, {scaleX: 0, transformOrigin: ruleOrigin});
    if (content) gsap.set(content, {x: offset, autoAlpha: 0});

    ScrollTrigger.create({
      trigger: group,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        const tl = gsap.timeline();
        if (rule) tl.to(rule, {scaleX: 1, duration: .6, ease: 'power2.out'}, 0);
        if (content) {
          // Same opacity-quicker-than-movement split as the hover circle:
          // the content is fully visible well before its slide finishes, so
          // the movement itself stays visible instead of hiding under the fade.
          tl.to(content, {autoAlpha: 1, duration: .3, ease: 'power1.out'}, 0);
          tl.to(content, {x: 0, duration: .6, ease: 'power2.out'}, 0);
        }
      },
    });
  });
}

function initSoloSlides(reducedMotion) {
  if (reducedMotion) return;

  gsap.utils.toArray('[data-slide-in]').forEach((el) => {
    const side = el.dataset.slideIn === 'right' ? 'right' : 'left';
    const offset = side === 'right' ? CONTENT_OFFSET : -CONTENT_OFFSET;

    gsap.set(el, {x: offset, autoAlpha: 0});

    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(el, {autoAlpha: 1, duration: .3, ease: 'power1.out'});
        gsap.to(el, {x: 0, duration: .6, ease: 'power2.out'});
      },
    });
  });
}

export function lineReveal() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  initLineGroups(reducedMotion);
  initSoloSlides(reducedMotion);
  initHoverLinks(reducedMotion);
}
